import React, { useEffect, useMemo, useState } from 'react';
import {
  CssBaseline,
  IconButton,
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Avatar,
  Chip,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Person,
  QuestionAnswerOutlined,
  ReceiptLongOutlined,
} from '@mui/icons-material';
import MuiDrawer from '@mui/material/Drawer';
import { styled } from '@mui/material/styles';
import Logo from '../../../assets/images/Voxtur-verify.png';
import { configuration, login, logout, setSelectedOffice } from '../../../store/slices/authSlice';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Breadcrumb from '../../common/Breadcrumb';
import HelpFab from '../../common/HelpFab';
import { resetBreadcrumb } from '../../../store/slices/breadcrumbSlice';
import { useLocation, useNavigate } from 'react-router';
import Loader from '../../common/Loader';
import { getConfigurations, orderLogout } from '../../../services/auth/auth';
import ProfileDrawer from '../../common/ProfileDrawer';
import chroma from 'chroma-js';
import { useQueryParams } from '../../../utils/filterUtils';
import LogoComponent from '../../admin/mainLayout/Logo';

// Defined at module scope (not inside OrderMainLayout) so `Drawer`/`DrawerHeader`
// keep a stable component identity across renders. `styled()` creates a new
// component type on every call — if these lived inside the component body,
// every re-render would give `<Drawer>` a new type, making React unmount/
// remount the whole sidebar DOM node and reset its scroll position.
const drawerWidth = 270;
const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});
const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `${window.innerWidth < 768 ? '0px' : `calc(${theme.spacing(4)}})`}`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(5)})`,
  },
});
const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));
const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  })
);

const OrderMainLayout = ({ children }) => {
  const url = window.location.origin;
  const location = useLocation();
  const { order } = useSelector((state) => state?.authSlice);
  const config = useSelector((state) => state.authSlice)?.configuration?.[url];
  const user = order?.first_name + ' ' + order?.last_name;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isReports = window.location.pathname.includes('reports');
  const logoUrl = useMemo(() => {
    return config?.company_logo_url ? config.company_logo_url : Logo;
  }, [config?.company_logo_url]);

  useEffect(() => {
    if (location.pathname.startsWith('/orders/reports')) {
      dispatch(
        login({
          data: { ...order, lastVisitedOrder: null },
          code: 'order',
        })
      );
    }
  }, [location.pathname]);

  useEffect(() => {
    if (Object.keys(config?.branding_json || {})?.length) {
      document.documentElement.style.setProperty(
        '--primary-color',
        config?.branding_json?.primary_color ?? ''
      );
      document.documentElement.style.setProperty(
        '--sidebar-color',
        config?.branding_json?.sidebar_color ?? ''
      );
      document.documentElement.style.setProperty(
        '--sidebar-font-color',
        config?.branding_json?.sidebar_font_color ?? ''
      );
      document.documentElement.style.setProperty(
        '--sidebar-font-darker-color',
        config?.branding_json?.sidebar_font_color
          ? chroma(config?.branding_json?.sidebar_font_color).darken(0.5).hex()
          : ''
      );
      document.documentElement.style.setProperty(
        '--sidebar-lighten-color',
        config?.branding_json?.sidebar_color
          ? chroma(config?.branding_json?.sidebar_color).brighten(0.5).hex()
          : ''
      );
      document.documentElement.style.setProperty(
        '--header-color',
        config?.branding_json?.header_color ?? ''
      );
      document.documentElement.style.setProperty(
        '--header-font-color',
        config?.branding_json?.header_font_color ?? ''
      );
      document.documentElement.style.setProperty(
        '--bg-color',
        config?.branding_json?.background_color ?? ''
      );
    }
  }, [config]);

  useEffect(() => {
    getConfigurationDetails();
  }, []);

  const getConfigurationDetails = async (updateConfig = false, ofc = null) => {
    setLoading(true);
    let body = { domain: url };
    if (ofc) {
      body.office_name = ofc;
    } else {
      body.office_name = config?.office_name?.replace(/ /g, '');
    }
    const res = await getConfigurations(body);
    if (res.success) {
      let activeOffices = res?.response?.filter((ofc) => ofc.status !== 'disabled');
      dispatch(configuration({ url, data: { ...(activeOffices?.[0] || {}) } }));
      if (updateConfig) {
        window.location.reload();
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (window.innerWidth < 768) {
      setOpen(false);
    }
  }, [window.innerWidth]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleLogout = async () => {
    setLoading(true);
    try {
      const resData = await orderLogout(order?.token);
      if (resData?.success) {
        dispatch(
          logout({
            code: 'order',
          })
        );
        dispatch(resetBreadcrumb());
        navigate('/login');
      } else {
        toast.error(resData?.error || resData?.message || 'Something went wrong!');
      }
    } catch (error) {
      toast.error('Something went wrong!');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    setOpen((open) => !open);
  };

  // const showResetFilters = useMemo(() => {
  //   const queryParams = getQueryParams();
  //   const filterableParams = Object.keys(queryParams).filter((key) => !EXCLUDED_KEYS.includes(key));
  //   return filterableParams.length > 0;
  // }, [getQueryParams]);

  // const handleResetAll = () => {
  //   const queryParams = getQueryParams();
  //   const searchParams = new URLSearchParams();

  //   // Preserve excluded keys while clearing others
  //   Object.entries(queryParams).forEach(([key, value]) => {
  //     if (EXCLUDED_KEYS.includes(key)) {
  //       searchParams.set(key, value);
  //     }
  //   });

  //   navigate({ search: searchParams.toString() }, { replace: true });
  // };

  const changeOffice = (e) => {
    dispatch(setSelectedOffice(e.target.value));
    const ofcName = order?.offices?.find((ofc) =>
      e.target.value === 'all' ? ofc.is_primary : ofc.client_office_id === e.target.value
    )?.office_name;
    if (ofcName) {
      getConfigurationDetails(true, ofcName?.replace(/ /g, ''));
    } else {
      window.location.reload();
    }
  };

  return (
    <>
      {loading && <Loader />}
      <CssBaseline />
      <div className='main-layout-div'>
        <Drawer
          variant='permanent'
          open={open}
          className='order-dashboard-sidebar modern-sidebar'
          style={{
            background: config?.branding_json?.sidebar_color
              ? config?.branding_json?.sidebar_color
              : '#fff',
          }}
        >
          <DrawerHeader>
            {open === true ? (
              <div className='d-flex align-items-center justify-between w-100p'>
                <LogoComponent src={logoUrl} />
                <IconButton
                  onClick={handleToggle}
                  className='toggle-btn'
                  size='small'
                  sx={{
                    ml: 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    },
                  }}
                >
                  <ChevronLeft fontSize='small' />
                </IconButton>
              </div>
            ) : (
              <IconButton
                onClick={handleToggle}
                className='toggle-btn-collapsed'
                size='small'
                sx={{
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  },
                }}
              >
                <ChevronRight fontSize='small' />
              </IconButton>
            )}
          </DrawerHeader>
          <Divider />
          <div className={`${open ? 'list-item-wrapper' : ''}`}>
            {/* <div>
            <button
              className='order-btn'
              onClick={() => {
                navigate('/orders/add');
              }}
            >
              + New Order
            </button>
          </div> */}
            <ListItem
              key={'Orders'}
              title={!open ? 'Orders' : ''}
              button
              onClick={() => navigate('/orders/order')}
              className={`${!location?.pathname?.includes('documentation') && !location?.pathname?.includes('requests') ? 'selected-item' : ''} sidebar-item`}
            >
              {
                <ListItemIcon>
                  <ReceiptLongOutlined fontSize='small' />
                </ListItemIcon>
              }
              <ListItemText primary={'Orders'} style={{ fontSize: '14px' }} />
            </ListItem>{' '}
            {/* CLIEN-208: Realtor feature disabled - Requests queue nav item hidden. */}
            {/* <ListItem
              key={'Requests'}
              title={!open ? 'Requests' : ''}
              button
              onClick={() => navigate('/orders/requests')}
              className={`${location?.pathname?.includes('requests') ? 'selected-item' : ''} sidebar-item`}
            >
              {
                <ListItemIcon>
                  <ReceiptLongOutlined fontSize='small' />
                </ListItemIcon>
              }
              <ListItemText primary={'Requests'} style={{ fontSize: '14px' }} />
            </ListItem> */}
            <ListItem
              key={'Documentation'}
              title={!open ? 'Help & FAQs' : ''}
              button
              onClick={() => navigate('/orders/documentation')}
              className={`${location?.pathname?.includes('documentation') ? 'selected-item' : ''} sidebar-item`}
            >
              {
                <ListItemIcon>
                  <QuestionAnswerOutlined fontSize='small' />
                </ListItemIcon>
              }
              <ListItemText primary={'Help & FAQs'} style={{ fontSize: '14px' }} />
            </ListItem>{' '}
            {/* <ListItem
              key={'Reports'}
              title={!open ? 'Reports' : ''}
              button
              onClick={() => navigate('/orders/reports')}
              className={`${isReports ? 'selected-item' : ''} sidebar-item`}
            >
              {
                <ListItemIcon>
                  <Assessment fontSize='small' />
                </ListItemIcon>
              }
              <ListItemText primary={'Reports'} style={{ fontSize: '14px' }} />
            </ListItem>{' '} */}
            {/* {bankAccountClient ? (
              <ListItem
                key={'Imported Orders'}
                title={!open ? 'Imported Orders' : ''}
                button
                onClick={() => navigate('/orders/imported-orders')}
                className={`${isImport ? 'selected-item' : ''} sidebar-item`}
              >
                {
                  <ListItemIcon>
                    <Upload fontSize='small' />
                  </ListItemIcon>
                }
                <ListItemText primary={'Imported Orders'} style={{ fontSize: '14px' }} />
              </ListItem>
            ) : (
              ''
            )} */}
          </div>
        </Drawer>
        <div className='main-layout-toolbar-div'>
          <Toolbar className='header-title main-layout-toolbar'>
            <div className='main-toolbar-block main-toolbar-left d-flex align-items-center'>
              {window.innerWidth > 550 && <Breadcrumb />}
            </div>
            <div
              className='main-toolbar-block main-toolbar-center d-flex justify-center align-items-center'
              style={{ maxWidth: '250px' }}
            >
              {/* {showResetFilters ? (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    startIcon={<Refresh />}
                    size='small'
                    // variant=''
                    onClick={handleResetAll}
                    className={'filter-reset-btn mr-16 f-14'}
                    sx={{ textTransform: 'none' }}
                  >
                    Reset Filters
                  </Button>
                </div>
              ) : (
                ''
              )} */}
              {order?.offices?.length > 1 && (
                <span className='office-toolbar-center'>
                  <span className='f-16 fw-500 mr-4'>Office :</span>
                  <Select
                    className='header-office-select'
                    value={order?.selected_office}
                    onChange={changeOffice}
                  >
                    <MenuItem value={'all'} key={'all'}>
                      All Offices
                    </MenuItem>
                    {order?.offices?.map((office) => (
                      <MenuItem value={office.client_office_id} key={office.client_office_id}>
                        {office.office_name}
                      </MenuItem>
                    ))}
                  </Select>
                </span>
              )}
            </div>
            <div className='main-toolbar-block main-toolbar-right d-flex flex-row justify-end align-items-center'>
              <div className='profile-chip'>
                <Chip
                  label={`Client : ${order?.company_name}`}
                  variant='outlined'
                  color='primary'
                  className='mr-16'
                />
              </div>
              <div className='profile-chip user-name'>
                <Chip
                  onClick={handleDrawerOpen}
                  avatar={
                    <Avatar
                      sx={{ width: '34px', height: '34px', fontSize: '16px' }}
                      className='mr-4 cursor-pointer bg-p'
                    >
                      <Person style={{ color: 'white' }} />
                    </Avatar>
                  }
                  label={user}
                  variant='outlined'
                  className='cursor-pointer'
                  color='primary'
                />
              </div>
            </div>
          </Toolbar>
          <div
            style={{
              flexGrow: 1,
              overflowY: 'auto',
            }}
          >
            {children}
          </div>
        </div>
        {/* <HelpFab /> */}
        <ProfileDrawer open={drawerOpen} onClose={handleDrawerClose} type='order' />
      </div>
    </>
  );
};

export default OrderMainLayout;
